import {
  createAsyncThunk,
  createSelector,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import { debounce } from "lodash-es";

import { regenerateSessionId } from "../session/sessionSlice";

import type { RootState } from "../../app/store";
import { SITUM_COLOR } from "../app/constants";
import {
  DEFAULT_GLYPH_SOURCE,
  DEFAULT_PADDING,
  DEFAULT_TRANSITION_DURATION,
  INITIAL_ZOOM,
  MAPBOXSTYLE_LIGHT,
  MAX_PITCH,
  MAX_ZOOM,
} from "./constants";
import { LayerIndex, type SliceProps } from "./types";

export const initialState: SliceProps = {
  baseMapStyle: MAPBOXSTYLE_LIGHT,
  controllerDisabled: false,
  cursor: null,
  externalFeatures: [],
  followUser: false,
  followUserLocked: false,
  fontSource: DEFAULT_GLYPH_SOURCE,
  hasUserInteracted: false,
  layerVisibility: Object.values(LayerIndex).reduce(
    (acc, layer) => ({ ...acc, [layer]: false }),
    {} as SliceProps["layerVisibility"],
  ),
  mapStyleLoaded: false,
  mapViewLimits: {
    bboxPadding: DEFAULT_PADDING,
    initialBearing: 0,
    initialLatitude: null,
    initialLongitude: null,
    initialPitch: 20,
    initialZoom: INITIAL_ZOOM,
    maxBearing: 359,
    maxPitch: 180,
    maxZoom: MAX_ZOOM,
    minBearing: 0,
    minPitch: 0,
    minZoom: 1,
  },
  mapViewState: {
    bearing: 0,
    latitude: 0,
    longitude: 0,
    maxPitch: MAX_PITCH,
    maxZoom: MAX_ZOOM,
    pitch: 20,
    transitionDuration: DEFAULT_TRANSITION_DURATION,
    transitionEasing: (t) =>
      ((t *= 2) <= 1 ? t * t * t : (t -= 2) * t * t + 2) / 2,
    zoom: INITIAL_ZOOM,
  },

  selectedFeatures: [],
  stylesheetId: "",
  trajectory: {
    currentIndex: { mustReset: false, value: 0 },
    data: [],
    followUser: false,
    pathColor: SITUM_COLOR,
    rangeFilter: {
      max: Infinity,
      min: 0,
    },
    speed: 1,
    status: "PAUSE",
    tooltip: "",
  },
};

// For async actions
export const setDebouncedMapViewState = createAsyncThunk(
  "map/setMapViewState",
  debounce(async (payload: SliceProps["mapViewState"], { dispatch }) => {
    dispatch(setMapViewState(payload));
  }, 200),
);

// Custom reducer
const layerVisiblityName = "map/setLayerVisibility";
export function setLayerVisibility(
  key: LayerIndex,
  value: boolean,
): PayloadAction<{ key: LayerIndex; value: boolean }> {
  return {
    payload: { key, value },
    type: layerVisiblityName,
  };
}
setLayerVisibility.type = layerVisiblityName;

export const MapSlice = createSlice({
  extraReducers: (builder) => {
    builder.addCase(setLayerVisibility, (state, action) => {
      state.layerVisibility[action.payload.key] = action.payload.value;
    });
    builder.addCase(regenerateSessionId, (state) => {
      state.hasUserInteracted = false;
    });
  },
  initialState,
  name: "map",
  reducers: {
    clearSelectedFeatures: (
      state: SliceProps,
      _action: PayloadAction<void>,
    ) => {
      state.selectedFeatures = [];
    },
    resetView: (
      state: SliceProps,
      action: PayloadAction<SliceProps["mapViewState"]["bearing"]>,
    ) => {
      state.mapViewState = {
        ...state.mapViewState,
        bearing: action?.payload || 0,
        pitch: 20,
        transitionDuration: DEFAULT_TRANSITION_DURATION,
        zoom: state.mapViewState.zoom - 0.00000001,
      };
    },
    setBaseMapStyle: (
      state: SliceProps,
      action: PayloadAction<SliceProps["baseMapStyle"]>,
    ) => {
      state.baseMapStyle = action.payload;
    },
    setControllerDisabled: (
      state: SliceProps,
      action: PayloadAction<boolean>,
    ) => {
      state.controllerDisabled = action.payload;
    },
    setCursor: (
      state: SliceProps,
      action: PayloadAction<SliceProps["cursor"]>,
    ) => {
      state.cursor = action.payload;
    },
    setExternalFeatures: (
      state: SliceProps,
      action: PayloadAction<SliceProps["externalFeatures"]>,
    ) => {
      state.externalFeatures = action.payload;
    },
    setFollowUser: (
      state: SliceProps,
      action: PayloadAction<SliceProps["followUser"]>,
    ) => {
      if (!state.followUserLocked) state.followUser = action.payload;
    },
    setFollowUserLocked: (
      state: SliceProps,
      action: PayloadAction<SliceProps["followUserLocked"]>,
    ) => {
      state.followUserLocked = action.payload;
    },
    setFontSource: (
      state: SliceProps,
      action: PayloadAction<SliceProps["fontSource"]>,
    ) => {
      state.fontSource = action.payload;
    },
    setMapStyleLoaded: (
      state: SliceProps,
      action: PayloadAction<SliceProps["mapStyleLoaded"]>,
    ) => {
      state.mapStyleLoaded = action.payload;
    },
    setMapViewLimits: (
      state: SliceProps,
      action: PayloadAction<SliceProps["mapViewLimits"]>,
    ) => {
      state.mapViewLimits = action.payload;
    },
    setMapViewState: (
      state: SliceProps,
      action: PayloadAction<SliceProps["mapViewState"]>,
    ) => {
      state.mapViewState = { ...state.mapViewState, ...action.payload };
    },
    setMaxZoomLimit: (
      state: SliceProps,
      action: PayloadAction<SliceProps["mapViewLimits"]["maxZoom"]>,
    ) => {
      state.mapViewLimits.maxZoom = action.payload;
    },
    setMinZoomLimit: (
      state: SliceProps,
      action: PayloadAction<SliceProps["mapViewLimits"]["minZoom"]>,
    ) => {
      state.mapViewLimits.minZoom = action.payload;
    },
    setStylesheetId: (
      state: SliceProps,
      action: PayloadAction<SliceProps["stylesheetId"]>,
    ) => {
      state.stylesheetId = action.payload;
    },
    setTrajectory: (
      state: SliceProps,
      action: PayloadAction<SliceProps["trajectory"]>,
    ) => {
      const {
        data,
        speed,
        status,
        followUser,
        tooltip,
        rangeFilter,
        currentIndex,
        pathColor,
      } = action.payload;
      if (data !== undefined) {
        state.trajectory.data = data;
      }
      if (speed !== undefined) {
        state.trajectory.speed = speed;
      }
      if (status !== undefined) {
        state.trajectory.status = status;
      }
      if (typeof followUser === "boolean") {
        state.trajectory.followUser = followUser;
      }
      if (tooltip !== undefined) {
        state.trajectory.tooltip = tooltip;
      }
      if (rangeFilter !== undefined) {
        state.trajectory.rangeFilter = {
          ...state.trajectory.rangeFilter,
          ...rangeFilter,
        };
      }
      if (currentIndex !== undefined) {
        state.trajectory.currentIndex = currentIndex;
      }
      if (pathColor !== undefined) {
        state.trajectory.pathColor = pathColor;
      }
    },
    setUserHasInteracted: (
      state: SliceProps,
      action: PayloadAction<boolean>,
    ) => {
      state.hasUserInteracted = action.payload;
    },
    toggleSelectedFeature: (
      state: SliceProps,
      action: PayloadAction<string>,
    ) => {
      if (state.selectedFeatures.includes(action.payload)) {
        const position = state.selectedFeatures.indexOf(action.payload);

        state.selectedFeatures.splice(position, 1);
      } else {
        state.selectedFeatures.push(action.payload);
      }
    },
  },
});

// Selectors
const sliceData = (state: RootState) => state.map;

export const selectMapViewState = createSelector(sliceData, (item) => {
  return item.mapViewState;
});

export const selectMapViewBearing = createSelector(sliceData, (item) => {
  return item.mapViewState.bearing;
});

export const selectControllerDisabled = createSelector(
  sliceData,
  (item) => item.controllerDisabled,
);

export const selectSelectedFeatures = createSelector(sliceData, (item) => {
  return item.selectedFeatures;
});
export const selectFollowUser = createSelector(sliceData, (item) => {
  return item.followUser;
});

export const selectMapViewLimits = createSelector(sliceData, (item) => {
  return item.mapViewLimits;
});

export const selectBaseMapStyle = createSelector(sliceData, (item) => {
  return item.baseMapStyle;
});

export const selectStyleSheetId = createSelector(sliceData, (item) => {
  return item.stylesheetId;
});

export const selectFontSource = createSelector(sliceData, (item) => {
  return item.fontSource;
});

export const selectMapStyleLoaded = createSelector(sliceData, (item) => {
  return item.mapStyleLoaded;
});

export const selectExternalFeatures = createSelector(sliceData, (item) => {
  return item.externalFeatures;
});

export const selectTrajectory = createSelector(sliceData, (item) => {
  return item.trajectory;
});

export const selectCursor = createSelector(sliceData, (item) => {
  return item.cursor;
});

export const selectHasUserInteracted = createSelector(
  sliceData,
  (item) => item.hasUserInteracted,
);

export const selectLayerVisibility = (key: LayerIndex) => (state: RootState) =>
  state.map.layerVisibility[key];

// Action creators are generated for each case reducer function
export const {
  clearSelectedFeatures,
  resetView,
  setBaseMapStyle,
  setControllerDisabled,
  setCursor,
  setExternalFeatures,
  setFollowUser,
  setFollowUserLocked,
  setFontSource,
  setMapStyleLoaded,
  setMapViewLimits,
  setMapViewState,
  setMaxZoomLimit,
  setMinZoomLimit,
  setStylesheetId,
  setTrajectory,
  setUserHasInteracted,
  toggleSelectedFeature,
} = MapSlice.actions;

export default MapSlice.reducer;
